import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { Icon } from "@iconify/react";
import { Button, Col, Divider, Row, Space } from "antd";
import Text from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";

import CreatePropertyModal from "js/components/Properties/ModernPropertyModal/CreatePropertyModal";
import { PropertyDetailsProps } from "js/components/Properties/types";
import { Property } from "js/components/types";

import { toTitleCase } from "../../../../../utils/utils";

const PropertyDetailsSection: React.FC<PropertyDetailsProps> = ({
  property,
  setPropertyData,
  readOnly = false,
}) => {
  const { propertyId } = useParams();
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");

  const showModal = () => {
    setSelectedPropertyId(propertyId || "");
    setIsPropertyModalOpen(true);
  };

  const handlePropertyUpdate = (updatedProperties: Property[]) => {
    const updatedProperty = updatedProperties.find((p) => p.id === propertyId);
    if (updatedProperty && updatedProperty.address) {
      const propertyDetails = {
        id: updatedProperty.id ?? undefined,
        alias: updatedProperty.alias,
        rental_price: Number(updatedProperty.rental_price),
        maintenance_price: Number(updatedProperty.maintenance_price),
        usage: updatedProperty.usage ?? undefined,
        address: {
          street: updatedProperty.address.street ?? "",
          exterior_number: updatedProperty.address.exterior_number ?? "",
          interior_number: updatedProperty.address.interior_number ?? undefined,
          zip_code: updatedProperty.address.zip_code ?? "",
          neighborhood: updatedProperty.address.neighborhood ?? "",
          city: updatedProperty.address.city ?? "",
          state: updatedProperty.address.state ?? "",
        },
        created_at: updatedProperty.created_at ?? undefined,
      };
      setPropertyData(propertyDetails);
    }
  };

  const convertToPropertyArray = (): Property[] => {
    if (!property || !property.address) return [];
    return [
      {
        id: property.id ?? null,
        alias: property.alias,
        rental_price: String(property.rental_price),
        maintenance_price: String(property.maintenance_price),
        usage: property.usage ?? null,
        address: {
          address: `${property.address.street} ${property.address.exterior_number}`,
          street: property.address.street,
          exterior_number: property.address.exterior_number,
          interior_number: property.address.interior_number ?? null,
          zip_code: property.address.zip_code,
          neighborhood: property.address.neighborhood,
          municipality: null,
          city: property.address.city,
          state: property.address.state,
        },
        landlord_id: property.landlord ?? null,
        created_at: property.created_at ?? null,
      },
    ];
  };

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Row align="middle" style={{ gap: 5 }}>
          <Icon fontSize={18} icon="lsicon:house-outline" />
          <Title level={5} style={{ margin: 0 }}>
            Datos del Inmueble
          </Title>
        </Row>
        {!readOnly && (
          <Button color={"geekblue"} variant={"filled"} onClick={showModal}>
            Editar
          </Button>
        )}
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Dirección
            </Text>
            <Text className="sentry-mask">
              {toTitleCase(
                `${property?.address?.street} ${property?.address?.exterior_number}`,
              )}
            </Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Alias del Inmueble
            </Text>
            <Text>{toTitleCase(`${property?.alias ?? "-"}`)}</Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Colonia
            </Text>
            <Text className="sentry-mask">{toTitleCase(`${property?.address?.neighborhood}`)}</Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Municipio / Ciudad
            </Text>
            <Text className="sentry-mask">{toTitleCase(`${property?.address?.city ?? ""}`)}</Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Estado
            </Text>
            <Text className="sentry-mask">{toTitleCase(`${property?.address?.state}`)}</Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Código Postal
            </Text>
            <Text className="sentry-mask">{toTitleCase(`${property?.address.zip_code}`)}</Text>
          </Space>
        </Col>
      </Row>
      <Divider />
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Row align="middle" style={{ gap: 5 }}>
          <Icon fontSize={18} icon="lsicon:house-outline" />
          <Title level={5} style={{ margin: 0 }}>
            Datos de Renta
          </Title>
        </Row>
      </Row>
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Valor mensual de renta
            </Text>
            <Text>
              {toTitleCase(
                `$${Number(property?.rental_price).toLocaleString()}`,
              )}
            </Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Valor mensual del mantenimiento
            </Text>
            <Text>
              {toTitleCase(
                `$${Number(property?.maintenance_price).toLocaleString()}`,
              )}
            </Text>
          </Space>
        </Col>
        <Col xs={24} md={12}>
          <Space direction="vertical" size={4}>
            <Text type="secondary" style={{ height: 5 }}>
              Uso del Inmueble
            </Text>
            <Text>{toTitleCase(`${property?.usage}`)}</Text>
          </Space>
        </Col>
      </Row>

      {!readOnly && (
        <CreatePropertyModal
          isOpen={isPropertyModalOpen}
          setIsOpen={setIsPropertyModalOpen}
          propertyId={selectedPropertyId}
          setPropertyId={setSelectedPropertyId}
          propertyData={convertToPropertyArray()}
          setPropertyData={handlePropertyUpdate}
        />
      )}
    </>
  );
};

export default PropertyDetailsSection;
